import { Text, Modal, InlineStack, BlockStack, Grid, Form, FormLayout, TextField, Button, ChoiceList, Divider } from '@shopify/polaris';
import { useState, useCallback, useEffect } from 'react';
import helpers from 'helpers/index';
import { useGetOrder, useMutateOrderPaid, useOrderClearTransaction } from 'queries/orders.query';
import { useNotification } from 'context/NotificationContext';
import __ from 'languages/index';
import dateandtime from 'date-and-time';
import QuickSearchUser from 'components/quickSearchUser';
import { TypedUser } from 'interface/user.model';
import { ResponsivePopover } from 'components/ResponsivePopover';
import { Player } from '@lottiefiles/react-lottie-player';
import OnlinePayment from 'media/lottie_files/Online Payment.json';
import { useField, useForm } from '@shopify/react-form';
import Capacity from 'components/Capacity';
import { ENUM_TRANSACTION_PAYMENT_METHOD } from 'queries/transactions.query';

export default function PaymentOrderModal({
  show,
  order_id,
  manifest_id,
  onClose,
}: {
  show: boolean;
  order_id: string;
  manifest_id?: string;
  onClose: (reload: boolean) => void;
}) {
  const { mutateAsync: clearTransactionInOrder, isPending: clearing } = useOrderClearTransaction();
  const { addNotification } = useNotification();
  const { data: orderData, isPending: loadingGetTicketViaOrderCode } = useGetOrder(order_id);
  const { mutateAsync: paidForThisOrder } = useMutateOrderPaid();

  /** Ai thu tiền? */
  const [popoverActive, setPopoverActive] = useState(false);
  const useFields = {
    /** create new one */
    casher: useField<string>({ value: '', validates: [] }),
    casher_name: useField<string>({ value: '', validates: [] }),
    payment_method: useField<string>({ value: 'CASH', validates: [] }),
    description: useField<string>({ value: '', validates: [] }),
  };

  const { fields, submit, submitting, dirty, makeClean, reset } = useForm({
    fields: useFields,
    async onSubmit(values) {
      try {
        await paidForThisOrder({
          order_id: BigInt(order_id),
          casher: values.casher ? BigInt(values.casher) : undefined,
          payment_method: values.payment_method,
          description: values.description,
        });

        onClose(true);
      } catch (e: any) {
        if (e.params !== undefined) {
          if (e.params.field !== undefined) {
            useFields[e.params.field].setError(__('check_this_field_again'));
          }
        }
      }
      return { status: 'success', errors: [] };
    },
  });

  /** Xoá record thanh toán của một bạn nào đó ... */
  const [askCount, setAskCount] = useState(0);
  const clearTransactionCallback = useCallback(
    async (order_id: string) => {
      setAskCount((old) => old + 1);
      if (askCount < 2) {
        return;
      }
      try {
        await clearTransactionInOrder(order_id);
        onClose(false);
      } catch (e) {}
      setAskCount(0);
    },
    [orderData, askCount]
  );

  return (
    <>
      <Modal
        size="large"
        open={show}
        title={`Thanh toán đơn hàng ${orderData?.order_code} ID #${orderData?.id} - ${orderData?.customer_data ? `${orderData?.customer_data?.display_name} - ${orderData?.customer_data?.user_phonenumber}` : 'Chưa có thông tin khách hàng'}`}
        onClose={() => onClose(false)}
      >
        <Modal.Section>
          <Form onSubmit={() => {}}>
            <Grid columns={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 2 }}>
              <Grid.Cell>
                <BlockStack gap="400">
                  <FormLayout.Group>
                    <TextField readOnly autoComplete="off" label="Khách hàng" value={orderData?.customer_data?.display_name} />
                    <TextField readOnly autoComplete="off" label="Số điện thoại" value={orderData?.customer_data?.user_phonenumber} />
                  </FormLayout.Group>
                  <FormLayout>
                    <TextField readOnly autoComplete="off" label="Điểm đi" value={orderData?.pickup_address} />
                    <TextField readOnly autoComplete="off" label="Điểm đến" value={orderData?.dropoff_address} />
                  </FormLayout>
                  <br />
                  <br />
                </BlockStack>
              </Grid.Cell>
              <Grid.Cell>
                <div>
                  <Text as="p">
                    <Text as="span" variant="headingLg">
                      {helpers.getPad(orderData?.total_seat)}
                    </Text>{' '}
                    vé /{' '}
                    <Text as="span" variant="headingLg">
                      {helpers.formatNumber(orderData?.total_must_pay || 0)} vnd
                    </Text>
                  </Text>
                </div>

                <br />

                {orderData?.transactions ? (
                  <BlockStack gap="400" inlineAlign="center">
                    <div style={{ width: '160px', height: '160px' }}>
                      <Player autoplay src={OnlinePayment} loop={true} />
                    </div>
                    <Text as="h3" variant="headingMd">
                      Vé đã được thanh toán ehhh!
                    </Text>

                    <Text as="p">
                      Thanh toán {helpers.formatNumber(orderData?.transactions?.amount)} qua hình thức {orderData?.transactions?.payment_method} lúc{' '}
                      {dateandtime.format(new Date(Number(orderData?.transactions?.createdAt)), 'HH:mm DD/MM/YYYY')}
                      <br />
                      Thu bởi{' '}
                      <strong>
                        {orderData?.transactions?.casher_data?.display_name} / {orderData?.transactions?.casher_data?.user_phonenumber}
                      </strong>
                      <br />
                      Nội dung: {orderData?.transactions?.description}
                    </Text>

                    <Capacity
                      current_user_can={'accountant'}
                      extra_condition={orderData?.transactions?.payment_method === ENUM_TRANSACTION_PAYMENT_METHOD.CASH}
                    >
                      <Divider />
                      {orderData?.is_exported_invoice === 0 ? (
                        <>
                          <Button fullWidth onClick={() => clearTransactionCallback(orderData?.id)} loading={clearing}>
                            {askCount === 0 ? 'Xoá khoản thanh toán này?' : askCount === 1 ? 'Bạn chắc chắn chứ?' : 'Bấm một lần nữa để xóa!'}
                          </Button>
                          <Text as="p">
                            Nếu có điều gì đó xảy ra như thanh toán nhầm, tài xế thu nhầm thì có thể xóa khoản thanh toán liên quan tới vé này.
                          </Text>
                        </>
                      ) : null}
                    </Capacity>
                  </BlockStack>
                ) : (
                  <BlockStack gap="400">
                    <ChoiceList
                      title="Hình thức thanh toán"
                      choices={[
                        {
                          label: 'Tiền mặt',
                          value: 'CASH',
                        },
                        {
                          label: 'Chuyển khoản ngân hàng',
                          value: 'BANK',
                          helpText: 'Chỉ được phép chuyển vào tài khoản công ty.',
                        },
                        {
                          label: 'Công nợ khách hàng',
                          value: 'DEBT_CUSTOMER',
                          helpText: 'Khách lẻ nợ.',
                        },
                        {
                          label: 'Công nợ đại lý',
                          value: 'DEBT_AGENT',
                          helpText: 'Dành cho tài khoản công nợ đại lý.',
                        },
                        {
                          label: 'Công nợ bên thứ ba',
                          value: 'THIRD_PARTY',
                          helpText: 'Bên thứ ba như Vé xe rẻ, An Vui.',
                        },
                      ]}
                      selected={[useFields.payment_method.value]}
                      onChange={(value) => useFields.payment_method.onChange(value.pop())}
                    />

                    <TextField autoComplete="off" label="Ghi chú" multiline={2} {...useFields.description} />

                    <ResponsivePopover
                      title="Ai thu tiền cho đơn này" // Title cho Modal trên di động
                      preferredPosition="below"
                      autofocusTarget="first-node"
                      active={popoverActive}
                      activator={
                        <InlineStack gap="400">
                          <Text as="p">Đã thu tiền bởi: </Text>
                          <Button disclosure variant="plain" onClick={() => setPopoverActive(!popoverActive)}>
                            {useFields.casher_name.value || 'Chưa có ai, click để thêm!'}
                          </Button>
                        </InlineStack>
                      }
                      onClose={() => setPopoverActive(false)}
                    >
                      <QuickSearchUser
                        title={''}
                        includeMe
                        user_role="driver,staff,sale,sale_admin,admin,super_admin"
                        onClose={(u: TypedUser) => {
                          useFields.casher.onChange(u.user_id);
                          useFields.casher_name.onChange(u.display_name);
                          setPopoverActive(false);
                        }}
                      />
                    </ResponsivePopover>

                    <Button tone="success" variant="secondary" onClick={submit} loading={submitting} disabled={submitting}>
                      Cập nhật
                    </Button>
                  </BlockStack>
                )}
              </Grid.Cell>
            </Grid>
          </Form>
        </Modal.Section>
      </Modal>
    </>
  );
}

